
import { useState } from "react";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { NavCategory } from "./NavCategory";
import { NavItem, NavItemChild } from "./NavItem";
import { AnimatePresence, motion } from "framer-motion";
import { NavSeparator } from "./NavSeparator";

interface MobileNavigationProps {
  userRole?: string;
}

export const MobileNavigation = ({ userRole }: MobileNavigationProps) => {
  const [open, setOpen] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();
  
  const { 
    categories, 
    categorizedItems, 
    isActive,
    isExpanded,
    toggleExpanded,
    navigate: navigateTo
  } = useNavigationItems(userRole);
  
  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    setOpen(false);
  };
  
  const handleNavigate = (path: string) => {
    navigateTo(path);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="fixed top-4 left-4 z-50 md:hidden"
        >
          <Menu size={24} />
          <span className="sr-only">Open Navigation</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="p-0 w-[280px] max-w-[90vw]">
        <SheetHeader className="h-16 border-b border-slate-200 px-4 flex items-center">
          <SheetTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center text-white font-bold text-lg">
              A
            </div>
            <span className="text-lg font-semibold text-slate-900">Dashboard</span>
          </SheetTitle>
        </SheetHeader>
        
        <div className="p-4 overflow-y-auto max-h-[calc(100vh-8rem)]">
          {categories.map((category, index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
            >
              {index > 0 && <NavSeparator />}
              <NavCategory label={category} collapsed={false} />
              {categorizedItems[category].map((item) => (
                <div key={item.id}>
                  <NavItem
                    item={item}
                    active={isActive(item)}
                    onClick={() => {
                      if (!item.children?.length) {
                        handleNavigate(item.path);
                      }
                    }}
                    onToggleExpand={() => toggleExpanded(item.id)}
                    isExpanded={isExpanded(item.id)}
                  />
                  
                  {item.children && item.children.length > 0 && (
                    <AnimatePresence>
                      {isExpanded(item.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          {item.children.map((child) => (
                            <NavItemChild
                              key={child.id}
                              item={child}
                              active={isActive(child)}
                              onClick={() => handleNavigate(child.path)}
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </motion.div>
          ))}
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-200 p-4">
          <Button 
            variant="destructive" 
            className="w-full flex items-center gap-2" 
            onClick={handleSignOut}
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
